import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// state management
import { useSelector } from 'react-redux';

// components
import { IRViewer, Colorbar } from '@fluke/irviewer';
import { debounce, unpackImageInfo } from '@fluke/irviewer/utilities';
import { LevelSpanBar } from './components/ToolsIRViewer';
import EditorToolbar from './components/EditorToolbar';

import PrimaryNavigation from '../../components/header/PrimaryNavigation';

import { Carousel } from './components';
import PanelImageInfo from './components/imageInfo';
import SidebarEditor from './components/Sidebar';
import { SavePromptOnExit } from '../../components/modals/SaveImages';
import handleImageLoad from './utilities/handleImageLoad';
import { UserContext } from '../../context/UserProvider';
import { getAllReports } from '../../store/slices/reportSlice';
import { useAppDispatch } from '../../store/configureStore';

export default function ImageEditorBatch() {
  const { userSettings: { temperatureUnits = 'F', currentOrganization } = {} } =
    useContext(UserContext);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAllReports());
  }, []);

  // payload
  const { selectedImageIds = [] } = useSelector((state) => state.images);
  const [loadedData, setLoadedData] = useState([]);

  // image settings
  const [controlSettings, setControlSettings] = useState({});
  const [imageSettings, setImageSettings] = useState({});
  const [groupImageData, setGroupImageData] = useState({});

  // active images
  const [firstImageId, setFirstImageId] = useState('');
  const [activeImages, setActiveImages] = useState([]);

  // palette
  const [palettesAvailable, setPalettesAvailable] = useState([]);
  const [paletteGradient, setPaletteGradient] = useState('');

  // ui
  const [activeMarker, setActiveMarker] = useState('');
  const [imageScale, setImageScale] = useState(1);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const getSettings = useCallback(
    (imageInfo = {}) => {
      let settings = {};

      if (Object.keys(imageInfo).length > 0) {
        settings = unpackImageInfo(imageInfo, temperatureUnits);
      }
      return settings;
    },
    [temperatureUnits],
  );

  const getCurrentImageData = useCallback(
    (imageId) => {
      const imageData = groupImageData?.[imageId] || {};
      const { PALETTE_NAMES = [], PALETTE_GRADIENT = '' } = imageData;

      // update available palettes dropdown
      setPalettesAvailable(PALETTE_NAMES);
      setPaletteGradient(PALETTE_GRADIENT);

      return imageData;
    },
    [groupImageData],
  );

  const loadSettingsById = (imageId) => {
    const nextImageData = getCurrentImageData(imageId);
    updateSettings(nextImageData);
  };

  const updateSettings = useCallback((nextSettings = {}) => {
    if (Object.keys(nextSettings).length > 0) {
      setControlSettings((prev) => ({ ...prev, ...nextSettings }));
    }
  }, []);

  const updateGroupImageData = (imageId = null, payload = {}) => {
    if (imageId && Object.keys(payload).length > 0) {
      setGroupImageData((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          ...payload,
        },
      }));
    }
  };

  const updateImageName = useCallback((imageId = '', nextName = '') => {
    const lastSortInfo = groupImageData?.[imageId];
    updateGroupImageData(imageId, {
      sort_info: {
        ...lastSortInfo,
        origin_path: nextName,
      },
    });
  }, []);

  const updateImageSettings = useCallback(
    // update irviewer settings
    // 300 ms debounce
    debounce((nextSettings) => {
      setImageSettings(nextSettings);
    }, 0),
    [],
  );

  const handleUpdateFromIRViewer = useCallback((payload = {}) => {
    // callback from the irviewer component
    const { DATA = {} } = payload;
    const { imageId } = DATA;

    if (!imageId) return;

    // update local imageInfo Store
    updateGroupImageData(imageId, payload);
  }, []);

  const handleActiveMarkerTool = useCallback((nextMarker) => {
    setActiveMarker(nextMarker);
  }, []);

  const handleImageScale = useCallback((nextValue) => {
    const nextScale = +nextValue.toFixed(2);
    setImageScale(nextScale);
  }, []);

  const sortedImages =
    useMemo(() => {
      const nextSort = loadedData.slice();

      if (activeImages.length > 0) {
        nextSort.sort((a, b) => activeImages.indexOf(a.id) - activeImages.indexOf(b.id));
      }

      return nextSort;
    }, [loadedData, activeImages]) || [];

  const handleActiveImages = useCallback(
    (nextImages = []) => {
      // when active images changes
      const nextFirstId = nextImages[0] ?? '';

      if (nextFirstId && firstImageId !== nextFirstId) {
        // first image id has changed
        setFirstImageId(nextFirstId);
        loadSettingsById(nextFirstId);
      }

      setActiveImages(nextImages);

      if (nextImages.length === 1) {
        // when only one image is active, force image scale to 1
        setImageScale(1);
      }
    },
    [firstImageId],
  );

  const firstImageData =
    useMemo(() => {
      // id or image data has changed
      return getCurrentImageData(firstImageId);
    }, [firstImageId, groupImageData]) || {};

  useEffect(() => {
    // on page load, load images
    handleImageLoad({ selectedImageIds, setLoadedData, updateGroupImageData, currentOrganization });
  }, [selectedImageIds]);

  useEffect(() => {
    // update settings whenever activeImageInfo has changed
    const imageInfo = { ...(firstImageData?.DATA?.json ?? {}) };

    if (Object.keys(imageInfo).length > 0) {
      const nextSettings = getSettings(imageInfo);

      updateSettings(nextSettings);
    }
  }, [firstImageData, getSettings, updateSettings]);

  useEffect(() => {
    // update irviewer settings
    if (Object.keys(controlSettings).length > 0) {
      // update after optional debounce timeout
      updateImageSettings(controlSettings);
    }
  }, [controlSettings]);

  useEffect(() => {
    // screen size listener
    const handleResize = () => {
      if (window.innerWidth < 1500) {
        setIsSmallScreen(true);
      } else {
        setIsSmallScreen(false);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div style={styles.page}>
      <SavePromptOnExit groupImageData={groupImageData} />
      <PrimaryNavigation />

      <div style={styles.ireditor}>
        <Carousel order={selectedImageIds} images={groupImageData} onChange={handleActiveImages} />

        <div style={styles.body}>
          <div style={styles.column}>
            <EditorToolbar
              settings={controlSettings}
              onChange={updateSettings}
              onActiveMarkerTool={handleActiveMarkerTool}
              imageScale={imageScale}
              handleImageScale={handleImageScale}
            />

            <div style={styles.irviewer}>
              <div style={styles.levelspan}>
                <LevelSpanBar
                  settings={controlSettings}
                  onChange={updateSettings}
                  linearGradient={paletteGradient}
                />
              </div>
              {/* ACTIVE IMAGES BEING EDITED */}
              <div style={styles.column}>
                <div
                  style={{ ...styles.images, width: isSmallScreen ? 640 : undefined }}
                  id="batchImageEditor"
                >
                  {sortedImages.map(({ id, data }, index) => (
                    <IRViewer
                      key={`image-${id ?? index}`}
                      config={{
                        width: 640,
                        height: 480,
                        scale: imageScale,
                        hidden: !activeImages.includes(id),
                        debounce: true, // used to prevent flash of old settings
                        delay: 200,
                      }}
                      data={data || {}}
                      settings={activeImages.includes(id) && imageSettings}
                      onChange={handleUpdateFromIRViewer}
                      activeMarker={activeMarker}
                      temperatureUnits={temperatureUnits}
                    />
                  ))}
                </div>
                {activeImages.length === 1 && (
                  <div style={{ maxWidth: 640, width: '100%' }}>
                    <PanelImageInfo
                      imageData={firstImageData}
                      settings={controlSettings}
                      photonotes={firstImageData?.photonotes}
                      onChange={updateSettings}
                    />
                  </div>
                )}
              </div>
              <div style={styles.colorbar}>
                <Colorbar
                  min={controlSettings?.paletteRangeMinTempC}
                  max={controlSettings?.paletteRangeMaxTempC}
                  linearGradient={paletteGradient}
                  temperatureUnit={controlSettings?.temperatureUnit}
                  height={480 + 4} // Had to do with the border thickness
                  width={15}
                  color="white"
                  fontWeight="normal"
                />
              </div>
            </div>
          </div>
        </div>
        <div style={styles.sidebar}>
          <SidebarEditor
            settings={controlSettings}
            activeImages={activeImages}
            firstImageData={firstImageData}
            groupImageData={groupImageData}
            palettes={palettesAvailable}
            onChange={updateSettings}
            onNameChange={updateImageName}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  row: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  page: {
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    backgroundColor: 'rgb(18, 18, 18)',
  },

  ireditor: {
    // irviewer + sidebars
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
  },
  body: {
    // canvas area, slider, colorbar,
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: '18px 0',
  },
  irviewer: {
    // canvas area, slider, colorbar,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    padding: '18px 0',
    margin: '0 18px',
    justifyContent: 'center',
  },
  colorbar: {
    paddingRight: 10,
  },
  images: {
    // grid style
    minWidth: 640,
    minHeight: 480,
    maxHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 18,
    padding: '0 8px',
    justifyContent: 'center',
  },

  sidebar: {
    overflow: 'auto',
    minWidth: '250px',
    maxWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(255, 255, 255,0.05)',
  },
  levelspan: {
    maxHeight: 480,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-end',
  },
};
